/**
 * Friend Request Popup Component
 *
 * DATE: 2025-12-22 | Author: IA + Luiz Couto
 *
 * A notification-style popup that appears when an AI Companion sends
 * a friend request to the user during a game.
 *
 * Features:
 * - Shows AI avatar and name
 * - Accept button (green)
 * - Decline button (subtle)
 * - Close button (X) to dismiss/ignore (request stays pending)
 * - Clicking outside the popup also dismisses it
 * - Auto-dismiss after 30 seconds if no action taken
 */

import { respondToFriendInvite } from '../api/friendsApi';
import { ChatService } from '../services/ChatService';

// ============================================================================
// TYPES
// ============================================================================

export interface FriendRequestData {
  saidId: number;
  fromNetcartasId: number;
  fromNickname: string;
  isAI?: boolean;
  requestedAt?: number;
}

export interface PopupCallbacks {
  onAccepted?: (request: FriendRequestData) => void;
  onDeclined?: (request: FriendRequestData) => void;
  onDismissed?: (request: FriendRequestData) => void;
}

// ============================================================================
// POPUP CLASS
// ============================================================================

export class FriendRequestPopup {
  private popupElement: HTMLElement | null = null;
  private currentRequest: FriendRequestData | null = null;
  private callbacks: PopupCallbacks = {};
  private myNetcartasId: number | null = null;
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

  // Auto-dismiss after 30 seconds
  private static readonly AUTO_DISMISS_MS = 30000;

  constructor() {
    this.createPopupElement();
    this.setupEventListeners();
  }

  /**
   * Set the current user's identity (needed for API calls)
   */
  public setMyIdentity(netcartasId: number): void {
    this.myNetcartasId = netcartasId;
    console.log(`🤝 [2025-12-22] FriendRequestPopup: Identity set - ${netcartasId}`);
  }

  /**
   * Set callbacks for popup actions
   */
  public setCallbacks(callbacks: PopupCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Show the popup with a friend request
   */
  public show(request: FriendRequestData): void {
    console.log(`🤝 [2025-12-22] FriendRequestPopup: Showing popup for ${request.fromNickname}`);

    this.currentRequest = request;
    this.render();

    if (this.popupElement) {
      this.popupElement.classList.remove('hidden');
      // Trigger animation
      requestAnimationFrame(() => {
        this.popupElement?.classList.add('visible');
      });
    }

    // DATE: 2025-12-22 | Disabled auto-dismiss - user requested to keep popup open until action
    // this.startAutoDismissTimer();
  }

  /**
   * Hide the popup
   */
  public hide(): void {
    this.clearAutoDismissTimer();

    if (this.popupElement) {
      this.popupElement.classList.remove('visible');
      // Wait for animation to complete
      setTimeout(() => {
        this.popupElement?.classList.add('hidden');
        this.currentRequest = null;
      }, 300);
    }
  }

  /**
   * Check if popup is currently visible
   */
  public isVisible(): boolean {
    return this.popupElement?.classList.contains('visible') || false;
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Create the popup DOM element
   */
  private createPopupElement(): void {
    // Create container
    this.popupElement = document.createElement('div');
    this.popupElement.className = 'friend-request-popup hidden';
    this.popupElement.innerHTML = `
      <div class="friend-request-popup-backdrop"></div>
      <div class="friend-request-popup-content">
        <button class="friend-request-popup-close" aria-label="Fechar">×</button>
        <div class="friend-request-popup-body">
          <div class="friend-request-popup-avatar">
            <div class="friend-request-popup-avatar-initials"></div>
          </div>
          <div class="friend-request-popup-info">
            <div class="friend-request-popup-title">Convite de Amizade</div>
            <div class="friend-request-popup-name"></div>
            <div class="friend-request-popup-subtitle">quer ser seu amigo</div>
          </div>
          <div class="friend-request-popup-actions">
            <button class="friend-request-popup-btn friend-request-popup-btn-accept">
              <span class="btn-text">Aceitar</span>
              <span class="btn-spinner hidden"></span>
            </button>
            <button class="friend-request-popup-btn friend-request-popup-btn-decline">
              Recusar
            </button>
          </div>
        </div>
      </div>
    `;

    // Append to body
    document.body.appendChild(this.popupElement);
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.popupElement) return;

    // Close button
    const closeBtn = this.popupElement.querySelector('.friend-request-popup-close');
    closeBtn?.addEventListener('click', () => this.handleDismiss());

    // Backdrop click
    const backdrop = this.popupElement.querySelector('.friend-request-popup-backdrop');
    backdrop?.addEventListener('click', () => this.handleDismiss());

    // Accept button
    const acceptBtn = this.popupElement.querySelector('.friend-request-popup-btn-accept');
    acceptBtn?.addEventListener('click', () => this.handleAccept());

    // Decline button
    const declineBtn = this.popupElement.querySelector('.friend-request-popup-btn-decline');
    declineBtn?.addEventListener('click', () => this.handleDecline());
  }

  /**
   * Render the popup content
   */
  private render(): void {
    if (!this.popupElement || !this.currentRequest) return;

    const request = this.currentRequest;

    // Update avatar initials
    const initials = this.getInitials(request.fromNickname);
    const avatarInitials = this.popupElement.querySelector('.friend-request-popup-avatar-initials');
    if (avatarInitials) {
      avatarInitials.textContent = initials;
    }

    // Update name
    const nameEl = this.popupElement.querySelector('.friend-request-popup-name');
    if (nameEl) {
      nameEl.textContent = request.fromNickname;
    }

    // Reset button states
    const acceptBtn = this.popupElement.querySelector('.friend-request-popup-btn-accept') as HTMLButtonElement;
    const declineBtn = this.popupElement.querySelector('.friend-request-popup-btn-decline') as HTMLButtonElement;

    if (acceptBtn) {
      acceptBtn.disabled = false;
      const btnText = acceptBtn.querySelector('.btn-text');
      const btnSpinner = acceptBtn.querySelector('.btn-spinner');
      if (btnText) btnText.classList.remove('hidden');
      if (btnSpinner) btnSpinner.classList.add('hidden');
    }

    if (declineBtn) {
      declineBtn.disabled = false;
    }
  }

  /**
   * Handle accept button click
   */
  private async handleAccept(): Promise<void> {
    if (!this.currentRequest || !this.myNetcartasId) {
      console.error('❌ [2025-12-22] FriendRequestPopup: Cannot accept - missing data');
      return;
    }

    const request = this.currentRequest;
    console.log(`✅ [2025-12-22] FriendRequestPopup: Accepting request from ${request.fromNickname}`);

    // Show loading state
    const acceptBtn = this.popupElement?.querySelector('.friend-request-popup-btn-accept') as HTMLButtonElement;
    const declineBtn = this.popupElement?.querySelector('.friend-request-popup-btn-decline') as HTMLButtonElement;

    if (acceptBtn) {
      acceptBtn.disabled = true;
      const btnText = acceptBtn.querySelector('.btn-text');
      const btnSpinner = acceptBtn.querySelector('.btn-spinner');
      if (btnText) btnText.classList.add('hidden');
      if (btnSpinner) btnSpinner.classList.remove('hidden');
    }
    if (declineBtn) {
      declineBtn.disabled = true;
    }

    try {
      // Call API to accept friend request
      // DATE: 2025-12-22 | Fixed: API requires (userId, friendId, saidId, accept, fromNickname)
      const result = await respondToFriendInvite(
        this.myNetcartasId,       // userId - quem está aceitando
        request.fromNetcartasId,  // friendId - quem enviou o convite
        request.saidId,           // saidId - ID do convite
        true,                     // accept
        request.fromNickname      // para mensagem de boas-vindas do AI
      );

      if (result.success) {
        console.log(`✅ [2025-12-22] FriendRequestPopup: Request accepted successfully`);
        // DATE: 2025-12-22 | Remove from pending requests so it doesn't show again on refresh
        ChatService.getInstance().removePendingRequest(request.saidId);
        this.callbacks.onAccepted?.(request);
      } else {
        console.error(`❌ [2025-12-22] FriendRequestPopup: API error:`, result.error);
        // Reset button state on error
        if (acceptBtn) {
          acceptBtn.disabled = false;
          const btnText = acceptBtn.querySelector('.btn-text');
          const btnSpinner = acceptBtn.querySelector('.btn-spinner');
          if (btnText) btnText.classList.remove('hidden');
          if (btnSpinner) btnSpinner.classList.add('hidden');
        }
        if (declineBtn) {
          declineBtn.disabled = false;
        }
        return;
      }
    } catch (error) {
      console.error(`❌ [2025-12-22] FriendRequestPopup: Error accepting:`, error);
      return;
    }

    this.hide();
  }

  /**
   * Handle decline button click
   */
  private async handleDecline(): Promise<void> {
    if (!this.currentRequest || !this.myNetcartasId) {
      console.error('❌ [2025-12-22] FriendRequestPopup: Cannot decline - missing data');
      return;
    }

    const request = this.currentRequest;
    console.log(`❌ [2025-12-22] FriendRequestPopup: Declining request from ${request.fromNickname}`);

    // Show loading state
    const acceptBtn = this.popupElement?.querySelector('.friend-request-popup-btn-accept') as HTMLButtonElement;
    const declineBtn = this.popupElement?.querySelector('.friend-request-popup-btn-decline') as HTMLButtonElement;

    if (acceptBtn) acceptBtn.disabled = true;
    if (declineBtn) declineBtn.disabled = true;

    try {
      // Call API to decline friend request
      // DATE: 2025-12-22 | Fixed: API requires (userId, friendId, saidId, accept)
      const result = await respondToFriendInvite(
        this.myNetcartasId,       // userId - quem está recusando
        request.fromNetcartasId,  // friendId - quem enviou o convite
        request.saidId,           // saidId - ID do convite
        false                     // decline
      );

      if (result.success) {
        console.log(`✅ [2025-12-22] FriendRequestPopup: Request declined successfully`);
        // DATE: 2025-12-22 | Remove from pending requests so it doesn't show again on refresh
        ChatService.getInstance().removePendingRequest(request.saidId);
        this.callbacks.onDeclined?.(request);
      } else {
        console.error(`❌ [2025-12-22] FriendRequestPopup: API error:`, result.error);
        // Reset button state on error
        if (acceptBtn) acceptBtn.disabled = false;
        if (declineBtn) declineBtn.disabled = false;
        return;
      }
    } catch (error) {
      console.error(`❌ [2025-12-22] FriendRequestPopup: Error declining:`, error);
      return;
    }

    this.hide();
  }

  /**
   * Handle dismiss (close button or backdrop click)
   */
  private handleDismiss(): void {
    if (!this.currentRequest) return;

    console.log(`👋 [2025-12-22] FriendRequestPopup: Dismissed (request stays pending)`);
    this.callbacks.onDismissed?.(this.currentRequest);
    this.hide();
  }

  /**
   * Start auto-dismiss timer
   * DATE: 2025-12-22 | Disabled - kept for future use if needed
   */
  // @ts-expect-error - Intentionally unused, kept for future use
  private startAutoDismissTimer(): void {
    this.clearAutoDismissTimer();
    this.autoDismissTimer = setTimeout(() => {
      console.log(`⏰ [2025-12-22] FriendRequestPopup: Auto-dismissed after ${FriendRequestPopup.AUTO_DISMISS_MS / 1000}s`);
      this.handleDismiss();
    }, FriendRequestPopup.AUTO_DISMISS_MS);
  }

  /**
   * Clear auto-dismiss timer
   */
  private clearAutoDismissTimer(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
  }

  /**
   * Get initials from a name
   */
  private getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}
